<template>
    <div>
        <audio
            ref="audio"
            @pause="soundPaused"
            @error="soundError"
            @ended="soundEnded"
            @loadedmetadata="updateDuration"
        ></audio>
        <input
            type="file"
            id="upload-sound"
            class="opacity-0 absolute w-1 h-1"
            accept=".wav"
            ref="uploadSound"
            @change="uploadSound"
        />

        <div class="flex justify-end gap-2 mb-4 xl:hidden">
            <vx-button
                color="muted-dark"
                outline
                data-test="export-mobile-button"
                :loading="loadingExport"
                @click="$emit('export')"
                size="sm"
            >
                Export
                <font-awesome-icon widthAuto :icon="faFileArrowDown" />
            </vx-button>
            <vx-button
                color="muted-dark"
                data-test="import-mobile-button"
                :loading="loadingImport"
                @click="$emit('import')"
                size="sm"
            >
                Import
                <font-awesome-icon widthAuto :icon="faFileArrowUp" />
            </vx-button>
        </div>

        <vx-table class="xl:grid-cols-10-auto" rowBreakpoint="xl">
            <vx-table-row isHeader class="xl:pr-0!">
                <div class="font-semibold">Order</div>
                <div class="font-semibold">Content</div>
                <div class="xl:col-span-6! font-semibold">Filename</div>
                <div class="font-semibold">Updated on</div>
                <div class="flex justify-end gap-2">
                    <vx-button
                        color="muted-dark"
                        outline
                        data-test="export-header-button"
                        :loading="loadingExport"
                        @click="$emit('export')"
                        size="sm"
                    >
                        Export
                        <font-awesome-icon widthAuto :icon="faFileArrowDown" />
                    </vx-button>
                    <vx-button
                        color="muted-dark"
                        data-test="import-header-button"
                        :loading="loadingImport"
                        @click="$emit('import')"
                        size="sm"
                    >
                        Import
                        <font-awesome-icon widthAuto :icon="faFileArrowUp" />
                    </vx-button>
                </div>
            </vx-table-row>

            <vx-table-row v-for="(sound, index) in parsedSounds" :key="index" class="relative overflow-hidden">
                <div
                    v-if="playing === sound.type"
                    class="absolute inset-0 bg-sky-100 animate-progress"
                    :style="{ animationDuration: audioDuration + 's' }"
                ></div>
                <vx-table-row-title class="relative z-10">
                    <meta-badge :icon="faWaveformLines">{{ sound.order }}</meta-badge>
                </vx-table-row-title>

                <vx-table-row-item class="relative z-10">
                    <template v-slot:label> Content </template>
                    <strong>
                        {{ sound.content }}
                    </strong>
                </vx-table-row-item>

                <vx-table-row-item class="xl:col-span-6! relative z-10">
                    <template v-slot:label> FileName </template>
                    {{ sound.filename }}
                </vx-table-row-item>

                <vx-table-row-item class="relative z-10">
                    <template v-slot:label> Updated at </template>
                    <vx-popover v-if="sound.updated_at" arrow placement="top" hover>
                        {{ fromNow(sound.updated_at) }}
                        <template v-slot:content>
                            {{ shortDateTime(sound.updated_at) }}
                        </template>
                    </vx-popover>
                </vx-table-row-item>

                <vx-table-row-buttons class="relative z-10">
                    <vx-button
                        @click="triggerUpload(sound.type)"
                        data-test="reupload-button"
                        color="muted"
                        class="w-full sm:w-auto"
                        outline
                    >
                        Re-upload
                        <font-awesome-icon widthAuto :icon="faRotate" />
                    </vx-button>
                    <vx-button
                        @click.prevent="toggleSound(sound.type)"
                        data-test="play-button"
                        color="muted-light"
                        class="w-full sm:w-auto"
                        :loading="loading === sound.type"
                    >
                        {{ playing === sound.type ? 'Pause' : 'Play' }}
                        <font-awesome-icon widthAuto :icon="playing === sound.type ? faPause : faVolumeHigh" />
                    </vx-button>
                </vx-table-row-buttons>
            </vx-table-row>
        </vx-table>
    </div>
</template>

<script>
import { parseSounds } from './soundsHelpers';
import { shiftToTextClient } from '~/services';
import {
    VxButton,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxPopover,
} from '@voxie/frontend-components';
import {
    faRotate,
    faVolumeHigh,
    faPause,
    faPlay,
    faWaveformLines,
    faFileArrowDown,
    faFileArrowUp,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { fromNow, shortDateTime } from '~/utils/date';

export default {
    components: {
        VxTable,
        VxTableRow,
        VxTableRowTitle,
        VxTableRowButtons,
        VxTableRowItem,
        VxButton,
        VxPopover,
        FontAwesomeIcon,
        MetaBadge,
    },
    props: {
        sounds: {
            type: Array,
            required: true,
        },
        loadingImport: {
            type: Boolean,
            default: false,
        },
        loadingExport: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: null,
            urls: {},
            uploadType: null,
            uploading: false,
            playing: null,
            audioDuration: 0,
        };
    },
    methods: {
        fromNow,
        shortDateTime,
        triggerUpload(type) {
            if (type && this.$refs.uploadSound) {
                this.uploadType = type;
                this.$refs.uploadSound.click();
            }
        },
        uploadSoundFileValid() {
            const file = this.uploadSoundFile();
            if (!file) {
                return false;
            }
            if (!file?.size) {
                return false;
            }
            const fileSize = file.size / 1024 / 1024; // in MB
            if (fileSize > 20) {
                this.$toasted.error('Your file is too large, the size limit of your wav file should not exceed 20MB');
                return false;
            }
            return true;
        },
        uploadSoundFile() {
            return this.$refs.uploadSound.files[0];
        },

        uploadSound() {
            if (!this.uploadSoundFileValid()) {
                return;
            }

            const file = this.uploadSoundFile();

            if (!file) {
                this.$refs.uploadSound.value = '';
                this.uploadType = null;
                return;
            }

            const teamId = Spark.state.currentTeam.id;

            this.uploading = true;
            this.$toasted.info("Don't close this window. Your file is being uploaded.");

            shiftToTextClient
                .uploadSound(teamId, this.uploadType, file)
                .then((response) => {
                    this.$toasted.success('Your file was successfully uploaded');
                    this.$emit('soundUpdated', response.data);
                })
                .catch((error) => {
                    if (error?.response?.status === 422) {
                        const responseErrors = error.response.data.errors;

                        Object.keys(responseErrors).forEach((key) => {
                            this.$toasted.error(responseErrors[key].join(' '));
                        });
                    } else {
                        this.$toasted.global.platform_error();
                    }
                })
                .finally(() => {
                    delete this.urls[this.uploadType];

                    this.$refs.uploadSound.value = '';

                    this.uploading = false;
                    this.uploadType = null;
                });
        },
        fetchSound(type) {
            const teamId = Spark.state.currentTeam.id;

            if (this.urls[type]) {
                return this.playSound(type);
            }

            this.loading = type;
            shiftToTextClient
                .sound(teamId, type)
                .then((response) => {
                    this.urls[type] = response.data.url;
                    this.playSound(type);
                })
                .catch(() => {
                    this.$toasted.global.platform_error();
                })
                .finally(() => {
                    this.loading = null;
                });
        },
        soundError() {
            delete this.urls[this.playing];
            this.fetchSound(this.playing);
        },
        soundPaused() {
            this.playing = null;
        },
        soundEnded() {
            this.playing = null;
        },
        updateDuration() {
            if (this.$refs.audio && this.$refs.audio.duration) {
                this.audioDuration = this.$refs.audio.duration;
            }
        },
        toggleSound(type) {
            if (this.playing === type) {
                this.pauseCurrentSound();
            } else {
                this.fetchSound(type);
            }
        },
        pauseCurrentSound() {
            this.$refs.audio.pause();
        },
        playSound(type) {
            if (!this.$refs.audio.paused) {
                this.$refs.audio.pause();
            }

            this.playing = type;

            this.$refs.audio.src = this.urls[type];
            this.$refs.audio.play();
        },
    },
    computed: {
        parsedSounds() {
            return parseSounds(this.sounds);
        },
        faRotate() {
            return faRotate;
        },
        faVolumeHigh() {
            return faVolumeHigh;
        },
        faPlay() {
            return faPlay;
        },
        faPause() {
            return faPause;
        },
        faWaveformLines() {
            return faWaveformLines;
        },
        faFileArrowDown() {
            return faFileArrowDown;
        },
        faFileArrowUp() {
            return faFileArrowUp;
        },
    },
};
</script>

<style scoped>
@keyframes progress {
    from {
        width: 0%;
    }
    to {
        width: 100%;
    }
}

.animate-progress {
    animation: progress linear forwards;
}
</style>
